import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const TOOLS = [
  { name: 'Paper Writer', desc: 'Generate academic papers, essays, and research documents with proper citations.', icon: 'ri-file-text-line', color: 'from-violet-500 to-violet-700', bg: 'bg-violet-50', path: '/paper-writer', tag: 'Academic' },
  { name: 'Genius Math', desc: 'Solve complex equations, show step-by-step solutions, and explain math concepts.', icon: 'ri-calculator-line', color: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50', path: '/genius-math', tag: 'STEM' },
  { name: 'Digital AI Search', desc: 'AI-powered research engine that finds, summarizes, and cites sources instantly.', icon: 'ri-search-eye-line', color: 'from-cyan-500 to-sky-600', bg: 'bg-cyan-50', path: '/digital-ai-search', tag: 'Research' },
  { name: 'Resume Builder', desc: 'Craft ATS-optimized resumes tailored to any job description in minutes.', icon: 'ri-profile-line', color: 'from-orange-500 to-amber-600', bg: 'bg-orange-50', path: '/resume-builder', tag: 'Career' },
  { name: 'Business Plan Writer', desc: 'Create investor-ready business plans with financial projections and market analysis.', icon: 'ri-briefcase-line', color: 'from-rose-500 to-pink-600', bg: 'bg-rose-50', path: '/business-plan-writer', tag: 'Business' },
  { name: 'eBook Creator', desc: 'Write, format, and publish professional eBooks on any topic with AI assistance.', icon: 'ri-book-open-line', color: 'from-indigo-500 to-violet-600', bg: 'bg-indigo-50', path: '/ebook-creator', tag: 'Publishing' },
  { name: 'Presentation Creator', desc: 'Design stunning slide decks with AI-generated content and beautiful layouts.', icon: 'ri-slideshow-line', color: 'from-fuchsia-500 to-pink-600', bg: 'bg-fuchsia-50', path: '/presentation-creator', tag: 'Design' },
  { name: 'Website Builder', desc: 'Generate complete, responsive websites from a simple text description.', icon: 'ri-global-line', color: 'from-teal-500 to-emerald-600', bg: 'bg-teal-50', path: '/website-builder', tag: 'Web' },
  { name: 'AI Image Generator', desc: 'Create stunning, high-resolution images from text prompts in seconds.', icon: 'ri-image-line', color: 'from-amber-500 to-orange-600', bg: 'bg-amber-50', path: '/ai-image-generator', tag: 'Creative' },
  { name: 'Master Architect', desc: 'Design architectural plans, floor layouts, and 3D concepts with AI precision.', icon: 'ri-building-2-line', color: 'from-slate-600 to-gray-700', bg: 'bg-slate-50', path: '/master-architect', tag: 'Architecture' },
  { name: 'AI Video', desc: 'Create stunning AI-generated videos for marketing, film, documentary, movies, and more.', icon: 'ri-video-line', color: 'from-red-500 to-rose-600', bg: 'bg-red-50', path: '/ai-video', tag: 'Video' },
];

interface ToolsDropdownProps {
  isDarkMode?: boolean;
}

export default function ToolsDropdown({ isDarkMode = false }: ToolsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Find current tool based on pathname
  const currentTool = TOOLS.find(tool => location.pathname === tool.path);

  const handleToolClick = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
          isDarkMode
            ? 'text-white/90 hover:bg-white/10 border border-white/20'
            : 'text-gray-700 hover:bg-gray-100 border border-gray-200'
        }`}
      >
        <i className="ri-apps-2-line text-base"></i>
        <span>Tools</span>
        <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-2 w-80 bg-white/98 backdrop-blur-xl border border-gray-200 rounded-2xl shadow-2xl overflow-hidden z-[100]">
          <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-violet-50 to-fuchsia-50">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-cpu-line text-violet-600 text-sm"></i>
              </div>
              <p className="text-xs font-bold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent uppercase tracking-widest">openclaw.ai Tools</p>
              <span className="ml-auto text-xs font-bold text-violet-600 bg-white px-2 py-0.5 rounded-full">{TOOLS.length}</span>
            </div>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {TOOLS.map((tool) => {
              const isActive = currentTool?.path === tool.path;
              return (
                <button
                  key={tool.name}
                  onClick={() => handleToolClick(tool.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer border-b border-gray-50 last:border-0 ${
                    isActive ? 'bg-gradient-to-r from-violet-50 to-fuchsia-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br ${tool.color} flex-shrink-0`}>
                    <i className={`${tool.icon} text-white text-base`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className={`text-sm font-bold truncate ${isActive ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent' : 'text-gray-800'}`}>
                        {tool.name}
                      </p>
                      <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full flex-shrink-0">
                        {tool.tag}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-1">{tool.desc}</p>
                  </div>
                  {isActive && (
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-violet-600 text-base"></i>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          <div className="px-4 py-3 border-t border-gray-100 bg-gradient-to-r from-violet-50 to-fuchsia-50">
            <div className="flex items-center gap-2">
              <div className="w-1 h-1 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 animate-pulse"></div>
              <p className="text-xs font-bold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                Powered by OpenClaw Algorithm
              </p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}